import { inject } from '@angular/core';
import { Routes } from '@angular/router';

import { AuthGuardService } from '@main-client/src/app/core/auth-guard.service';
import { GroupAdminGuardService } from '@main-client/src/app/core/group-admin-guard.service';
import { DevelopmentProgramsEnabledGuardService } from '@main-client/src/app/development-programs-enabled-guard/development-programs-enabled-guard.service';
import { GroupsLegacyGuardService } from '@main-client/src/app/groups/groups-legacy-guard.service';
import { EventsEnabledGuardService } from '@main-client/src/app/guards/events-enabled-guard.service';
import { PreregistrationLegacyGuardService } from '@main-client/src/app/sign-up/preregistration-legacy-guard.service';

import { GroupViewComponent } from '@main-client/src/app/group-view/group-view.component';
import { GroupMatchViewComponent } from '@main-client/src/app/groups/group-match-view/group-match-view.component';

import { ActiveUserGuard } from '@main-client/src/app/core/active-user.guard';
import { pathwaysGuard } from '@main-client/src/app/core/can-activate-guards';
import { MembersDirectoryPermissions } from '@main-client/src/app/core/members-directory-permissions';
import { GroupLoadedGuard } from '@main-client/src/app/groups/group-loaded.guard';
import { GroupsHomepageGuard } from '@main-client/src/app/groups/groups-homepage.guard';

export const groupRoutes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: '/hubs',
  },
  {
    canActivate: [
      PreregistrationLegacyGuardService,
      AuthGuardService,
      GroupLoadedGuard,
    ],
    canActivateChild: [ActiveUserGuard],
    children: [
      {
        canActivate: [GroupsHomepageGuard],
        children: [],
        path: '',
        pathMatch: 'full',
      },
      {
        children: [
          {
            loadChildren: () =>
              import(
                '../group-community-page-view/group-community-page-view.module'
              ).then((m) => m.GroupCommunityPageViewModule),
            path: 'community',
            title: $localize`:@@groupRoute.welcome:Welcome`,
          },
          {
            loadChildren: () =>
              import('../group-intro-view/group-intro-view.module').then(
                (m) => m.GroupIntroViewModule,
              ),
            path: 'introductions',
            title: $localize`:@@global.introductions:Introductions`,
          },
          {
            children: [
              {
                loadChildren: () =>
                  import(
                    '../group-intro-dashboard-view/group-intro-dashboard-view.module'
                  ).then((m) => m.GroupIntroDashboardViewModule),
                path: 'dashboard',
                title: $localize`:@@groupRoute.introductionsDashboard:Introductions dashboard`,
              },
              {
                loadChildren: () =>
                  import(
                    '../group-intro-schedule-view/group-intro-schedule-view.module'
                  ).then((m) => m.GroupIntroScheduleViewModule),
                path: 'introductions-schedule',
                title: $localize`:@@groupRoute.introductionsSchedule:Introductions schedule`,
              },
              {
                loadChildren: () =>
                  import(
                    '../group-intro-config-view/group-intro-config-view.module'
                  ).then((m) => m.GroupIntroConfigViewModule),
                path: 'introductions-configurations',
              },
              {
                loadChildren: () =>
                  import(
                    '../introduction-algorithm-overview/introduction-algorithm-overview.module'
                  ).then((m) => m.IntroductionAlgorithmOverviewModule),
                path: 'how-introduction-works',
              },
              {
                loadChildren: () =>
                  import(
                    '../group-intro-feedbacks-view/group-intro-feedbacks-view.module'
                  ).then((m) => m.GroupIntroFeedbacksViewModule),
                path: 'feedbacks',
              },
              {
                loadChildren: () =>
                  import(
                    '../group-intro-have-met-questions-view/group-intro-have-met-questions-view.module'
                  ).then((m) => m.GroupIntroHaveMetQuestionsViewModule),
                path: 'have-met-questions',
                title: $localize`:@@groupRoute.haveMetQuestions:Have met questions`,
              },
              {
                path: '',
                pathMatch: 'full',
                redirectTo: 'introductions-schedule',
              },
            ],
            path: 'manage-introductions',
          },
          {
            canActivate: [DevelopmentProgramsEnabledGuardService],
            loadChildren: () =>
              import(
                '../group-programs-user-view/group-programs-user-view.module'
              ).then((m) => m.GroupProgramsUserViewModule),
            path: 'programs',
            title: $localize`:@@global.developmentPrograms:Development Programs`,
          },
          {
            canActivate: [DevelopmentProgramsEnabledGuardService],
            loadChildren: () =>
              import(
                '../group-program-user-view/group-program-user-view.module'
              ).then((m) => m.GroupProgramUserViewModule),
            path: 'programs/:programId',
            title: $localize`:@@global.developmentProgram:Development Program`,
          },
          {
            canActivate: [DevelopmentProgramsEnabledGuardService],
            children: [
              {
                path: '',
                pathMatch: 'full',
                redirectTo: 'programs',
              },
              {
                loadChildren: () =>
                  import(
                    '../create-development-program-view/create-development-program-view.module'
                  ).then((m) => m.CreateDevelopmentProgramViewModule),
                path: 'create-template',
              },
              {
                loadChildren: () =>
                  import(
                    '../create-development-program-view/create-development-program-view.module'
                  ).then((m) => m.CreateDevelopmentProgramViewModule),
                path: 'edit-template',
              },
              {
                children: [
                  {
                    loadChildren: () =>
                      import(
                        '../group-development-program-templates-view/group-development-program-templates-view.module'
                      ).then(
                        (m) => m.GroupDevelopmentProgramTemplatesViewModule,
                      ),
                    path: '',
                    title: $localize`:@@groupRoute.programTemplates:Program templates`,
                  },
                ],
                path: 'templates',
              },
              {
                loadChildren: () =>
                  import(
                    '../group-programs-admin-list-view/group-programs-admin-list-view.module'
                  ).then((m) => m.GroupProgramsAdminListViewModule),
                path: 'programs',
              },
              {
                loadChildren: () =>
                  import(
                    '../group-development-programs-view/group-development-programs-view.module'
                  ).then((m) => m.GroupDevelopmentProgramsViewModule),
                path: ':programId',
                title: $localize`:@@global.developmentPrograms:Development Programs`,
              },
            ],
            path: 'manage-programs',
          },
          {
            path: 'events',
            title: $localize`:@@global.officeHours:Office Hours`,
            canActivate: [EventsEnabledGuardService],
            loadChildren: () =>
              import('../group-events-view/group-events-view.module').then(
                (m) => m.GroupEventsViewModule,
              ),
          },
          {
            path: 'events-engagement-campaigns',
            canActivate: [GroupAdminGuardService, EventsEnabledGuardService],
            title: $localize`:@@groupRoute.officeHourEngagementCampaigns:Office Hours engagement campaigns`,
            loadChildren: () =>
              import(
                '../group-event-engagement-campaigns-view/group-event-engagement-campaigns-view.module'
              ).then((m) => m.GroupEventEngagementCampaignsViewModule),
          },
          {
            path: 'events-settings',
            title: $localize`:@@groupRoute.officeHoursSettings:Office Hours settings`,
            canActivate: [EventsEnabledGuardService],
            loadChildren: () =>
              import(
                '../group-event-settings-view/group-event-settings-view.module'
              ).then((m) => m.GroupEventSettingsViewModule),
          },
          {
            loadChildren: () =>
              import('../group-event-view/group-event-view.module').then(
                (m) => m.GroupEventViewModule,
              ),
            path: 'event',
            title: $localize`:@@global.officeHours:Office Hours`,
          },
          {
            loadChildren: () =>
              import('../experiences-view/experiences-view.module').then(
                (m) => m.ExperiencesViewModule,
              ),
            path: 'experiences',
            title: $localize`:@@global.experiences:Experiences`,
          },
          {
            loadChildren: () =>
              import('../group-articles-view/group-articles-view.module').then(
                (m) => m.GroupArticlesViewModule,
              ),
            path: 'articles',
            title: $localize`:@@groupRoute.articles:Articles`,
          },
          {
            loadChildren: () =>
              import('../group-article-view/group-article-view.module').then(
                (m) => m.GroupArticleViewModule,
              ),
            path: 'article',
            title: $localize`:@@groupRoute.articles:Articles`,
          },
          {
            canActivate: [
              () => inject(MembersDirectoryPermissions).canActivate(),
            ],
            loadChildren: () =>
              import('../group-members-view/group-members-view.module').then(
                (m) => m.GroupMembersViewModule,
              ),
            path: 'members',
            title: $localize`:@@groupRoute.hubMembers:Hub members`,
          },
          {
            children: [
              {
                loadChildren: () =>
                  import('../group-admins-view/group-admins-view.module').then(
                    (m) => m.GroupAdminsViewModule,
                  ),
                path: 'admins',
                title: $localize`:@@groupRoute.hubAdmins:Hub admins`,
              },
              {
                loadChildren: () =>
                  import(
                    '../group-join-requests-view/group-join-requests-view.module'
                  ).then((m) => m.GroupJoinRequestsViewModule),
                path: 'join-requests',
                title: $localize`:@@groupRoute.hubJoinRequests:Hub join requests`,
              },
              {
                loadChildren: () =>
                  import(
                    '../group-manage-members-view/group-manage-members-view.module'
                  ).then((m) => m.GroupManageMembersViewModule),
                path: 'members',
                title: $localize`:@@groupRoute.manageHubMembers:Manage Hub members`,
              },
              {
                loadChildren: () =>
                  import(
                    '../group-edit-members-view/group-edit-members-view.module'
                  ).then((m) => m.GroupEditMembersViewModule),
                path: 'edit-members',
                title: $localize`:@@groupRoute.editHubMembers:Edit Hub members`,
              },
              {
                loadChildren: () =>
                  import(
                    '../group-existing-members-view/group-existing-members-view.module'
                  ).then((m) => m.GroupExistingMembersViewModule),
                path: 'new-members',
                title: $localize`:@@groupRoute.hubNewMembers:Hub new members`,
              },
              {
                loadChildren: () =>
                  import(
                    '../group-invites-view/group-invites-view.module'
                  ).then((m) => m.GroupInvitesViewModule),
                path: 'invites',
                title: $localize`:@@groupRoute.hubInvites:Hub invites`,
              },
              {
                loadChildren: () =>
                  import(
                    '../invitation-email-campaigns-view/invitation-email-campaigns-view.module'
                  ).then((m) => m.InvitationEmailCampaignsViewModule),
                path: 'invitation-email-campaigns',
                title: $localize`:@@groupRoute.invitationDripCampaigns:Invitation drip campaigns`,
              },
              {
                loadChildren: () =>
                  import(
                    '../group-csv-invites-view/group-csv-invites-view.module'
                  ).then((m) => m.GroupCsvInvitesViewModule),
                path: 'csv-invites',
                title: $localize`:@@groupRoute.hubCSVInvites:Hub CSV invites`,
              },
              {
                loadChildren: () =>
                  import(
                    '../group-sent-invites-view/group-sent-invites-view.module'
                  ).then((m) => m.GroupSentInvitesViewModule),
                path: 'sent-invites',
                title: $localize`:@@groupRoute.hubSentInvites:Hub sent invites`,
              },
              {
                path: '',
                pathMatch: 'full',
                redirectTo: 'members',
              },
            ],
            path: 'manage-members',
          },
          {
            loadChildren: () =>
              import(
                '../group-data-view-beta/group-data-view-beta.module'
              ).then((m) => m.GroupDataViewBetaModule),
            path: 'data',
            title: $localize`:@@groupRoute.hubData:Hub data`,
          },
          {
            loadChildren: () =>
              import(
                '../group-base-config-view/group-base-config-view.module'
              ).then((m) => m.GroupBaseConfigViewModule),
            path: 'base-config-templates',
          },
          {
            loadChildren: () =>
              import(
                '../group-email-communications-view/group-email-communications-view.module'
              ).then((m) => m.GroupEmailCommunicationsViewModule),
            path: 'email-communications',
            title: $localize`:@@groupRoute.emailCommunications:Email communications`,
          },
          {
            loadChildren: () =>
              import('../group-settings-view/group-settings-view.module').then(
                (m) => m.GroupSettingsViewModule,
              ),
            path: 'manage-settings',
            title: $localize`:@@groupRoute.hubSettings:Hub settings`,
          },
          {
            children: [
              {
                loadChildren: () =>
                  import(
                    '../group-membership-configurations-view/group-membership-configurations-view.module'
                  ).then((m) => m.GroupMembershipConfigurationsViewModule),
                path: 'membership-configurations',
                title: $localize`:@@groupRoute.membershipConfigurations:Membership configurations`,
              },
              {
                path: '',
                pathMatch: 'full',
                redirectTo: 'manage-settings',
              },
            ],
            path: 'manage-hub-membership',
          },
          {
            loadChildren: () =>
              import(
                '../group-translation-view/group-translation-view.module'
              ).then((m) => m.GroupTranslationViewModule),
            path: 'manage-hub-localization',
            title: $localize`:@@groupRoute.hubLocalization:Hub localization`,
          },
          {
            children: [
              {
                loadChildren: () =>
                  import(
                    '../group-community-page-form-view/group-community-page-form-view.module'
                  ).then((m) => m.GroupCommunityPageFormViewModule),
                path: 'community-page-configurations',
                title: $localize`:@@groupRoute.editWelcomePage:Edit welcome page`,
              },
              {
                path: '',
                pathMatch: 'full',
                redirectTo: 'manage-settings',
              },
            ],
            path: 'manage-community-page',
          },
          {
            path: 'member-settings',
            pathMatch: 'full',
            redirectTo: 'introductions',
          },
          {
            loadChildren: () =>
              import('../group-opt-out/group-opt-out.module').then(
                (m) => m.GroupOptOutModule,
              ),
            path: 'opt-out',
          },
          {
            loadChildren: () =>
              import(
                '../group-intro-connections-view/group-intro-connections-view.module'
              ).then((m) => m.GroupIntroConnectionsViewModule),
            path: 'connections',
            title: $localize`:@@groupRoute.connections:Connections`,
          },
          {
            path: 'pathways',
            pathMatch: 'full',
            redirectTo: 'manage-pathways',
          },
          {
            loadChildren: async () =>
              (await import('../pathway-user-view/pathway-user-view.module'))
                .PathwayUserViewModule,
            path: 'pathways/:pathwayId',
            title: $localize`:@@global.pathways:Pathways`,
          },
          {
            canActivate: [pathwaysGuard()],
            loadChildren: () =>
              import(
                '../group-pathways-admin-view/group-pathways-admin-view.module'
              ).then((m) => m.GroupPathwaysAdminViewModule),
            path: 'manage-pathways',
            title: $localize`:@@global.managePathways:Manage Pathways`,
          },
        ],
        component: GroupViewComponent,
        path: '',
      },
      {
        loadChildren: () =>
          import(
            '../group-intro-compliments-view/group-intro-compliments-view.module'
          ).then((m) => m.GroupIntroComplimentsViewModule),
        path: 'manage-introductions/compliments',
        title: $localize`:@@groupRoute.introductionsCompliments:Introductions compliments`,
      },
      {
        loadChildren: () =>
          import(
            '../group-goal-settings-view/group-goal-settings-view.module'
          ).then((m) => m.GroupGoalSettingsViewModule),
        path: 'goal-setting',
        title: $localize`:@@groupRoute.goalSetting:Goal setting`,
      },
      {
        component: GroupMatchViewComponent,
        path: 'introductions',
      },
      {
        canActivate: [GroupsLegacyGuardService],
        children: [],
        path: 'group-chat-manager',
      },
      {
        canActivate: [GroupsLegacyGuardService],
        children: [],
        path: 'content-manager',
      },
      {
        canActivate: [GroupsLegacyGuardService],
        children: [],
        path: 'create-opportunity',
      },
      {
        canActivate: [GroupsLegacyGuardService],
        children: [],
        path: 'edit-opportunity',
      },
      {
        children: [
          {
            canActivate: [GroupsLegacyGuardService],
            children: [],
            path: ':opportunitySlug',
          },
        ],
        path: 'opportunity',
      },
      {
        canActivate: [pathwaysGuard()],
        children: [
          {
            loadChildren: () =>
              import('../edit-pathway-view/edit-pathway-view.module').then(
                (m) => m.EditPathwayViewModule,
              ),
            path: 'edit',
            title: $localize`:@@groupRoute.editPathways:Edit Pathways`,
          },
        ],
        path: 'manage-pathways',
      },
    ],
    path: ':groupSlug',
  },
];
